import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { FiArrowDown } from 'react-icons/fi';
import Button from '../common/Button';
import { SITE_FULL_NAME } from '../../utils/constants';
import SafeHTML from '../common/SafeHTML';

const DEFAULT_SLIDE_DURATION = 10;

// Maps fraction strings to CSS column span in a 12-column grid
const COL_SPAN_MAP = { '1/4': 3, '1/3': 4, '1/2': 6, '2/3': 8, '3/4': 9, 'full': 12 };

// Normalize legacy integer colSpan values (from old 4-col grid) to fraction strings
const normalizeColSpan = (colSpan) => {
  if (typeof colSpan === 'string' && COL_SPAN_MAP[colSpan] !== undefined) return colSpan;
  const legacyMap = { 1: '1/4', 2: '1/2', 3: '3/4', 4: 'full' };
  return legacyMap[colSpan] || 'full';
};

const parseContentBlocks = (contentBlock) => {
  if (!contentBlock) return [];
  try {
    const parsed = JSON.parse(contentBlock);
    if (Array.isArray(parsed)) return parsed.map(b => ({ ...b, colSpan: normalizeColSpan(b.colSpan) }));
  } catch {}
  // Legacy single HTML string → render as one full-width block
  return [{ id: 'legacy', content: contentBlock, colSpan: 'full', textAlign: 'center', bgColor: '#ffffff', bgOpacity: 0, borderRadius: 0, borderWidth: 0, borderColor: '#cccccc', padding: 'md' }];
};

const hexToRgba = (hex, opacity) => {
  if (!hex || opacity === 0) return 'transparent';
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch {
    return 'transparent';
  }
};

const PADDING_MAP = { none: '0px', sm: '0.5rem', md: '1rem', lg: '1.5rem' };

function HeroSlide({ slide, currentSeries, backgroundY }) {
  const title = slide?.title || SITE_FULL_NAME;
  const subtitle = slide?.subtitle || '';
  const backgroundImage = slide?.background_image_url;
  const backgroundVideo = slide?.background_video_url?.trim() || null;

  const parallaxEnabled = slide?.parallax_enabled || false;
  const hasBackground = backgroundImage || backgroundVideo;
  const textColor = slide?.text_color || 'white';
  const contentBlock = slide?.content_block || '';
  const contentBlocks = parseContentBlocks(contentBlock);

  // Derive text class based on text_color when there's a background, else default to primary
  const titleClass = hasBackground
    ? (textColor === 'black' ? 'text-gray-900' : 'text-white drop-shadow-lg')
    : 'text-primary';
  const subtitleClass = hasBackground
    ? (textColor === 'black' ? 'text-gray-700' : 'text-white/90 drop-shadow')
    : 'text-primary/60';
  const contentBlockClass = hasBackground
    ? (textColor === 'black' ? 'text-gray-800' : 'text-white/90 drop-shadow')
    : 'text-primary/80';

  const cta1Text = slide?.cta1_text || '';
  const cta1Link = slide?.cta1_link || '';
  const cta1Variant = slide?.cta1_variant || '';

  const cta2Text = slide?.cta2_text || '';
  const cta2Link = slide?.cta2_link || '';
  const cta2Variant = slide?.cta2_variant || '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1, ease: 'easeInOut' }}
      className="absolute inset-0 flex items-center justify-center text-center px-4"
    >
      {/* Background Video (looping, muted, autoplay) */}
      {backgroundVideo && (
        <div className="absolute inset-0 overflow-hidden">
          <video
            key={backgroundVideo}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover scale-[1.02]"
          >
            <source src={backgroundVideo} />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50" />
        </div>
      )}

      {/* Background Image with Parallax */}
      {!backgroundVideo && backgroundImage && (
        <motion.div
          className="absolute inset-0"
          style={{
            y: parallaxEnabled ? backgroundY : undefined,
          }}
        >
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: 'scale(1.1)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40" />
        </motion.div>
      )}

      {/* Subtle background pattern (fallback when no image/video) */}
      {!backgroundVideo && !backgroundImage && (
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #072441 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>
      )}

      <div className="relative z-10 max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className={`font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-tight mb-6 ${titleClass}`}
          style={{ letterSpacing: '-0.02em' }}
        >
          <SafeHTML html={title} />
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className={`text-xl sm:text-2xl md:text-3xl font-light ${contentBlocks.length > 0 ? 'mb-6' : 'mb-10'} ${subtitleClass}`}
        >
          {subtitle}
        </motion.p>

        {contentBlocks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-10 grid grid-cols-1 md:grid-cols-12 gap-4 w-full"
          >
            {contentBlocks.map((block) => (
              <div
                key={block.id}
                data-col-span={COL_SPAN_MAP[block.colSpan] || 12}
                style={{
                  ['--col-span']: `span ${COL_SPAN_MAP[block.colSpan] || 12}`,
                  backgroundColor: hexToRgba(block.bgColor, block.bgOpacity),
                  borderRadius: `${block.borderRadius}px`,
                  border: block.borderWidth > 0
                    ? `${block.borderWidth}px solid ${block.borderColor}`
                    : 'none',
                  padding: PADDING_MAP[block.padding] || '1rem',
                  textAlign: block.textAlign,
                }}
                className={`col-span-full md:[grid-column:var(--col-span)] prose prose-sm max-w-none ${contentBlockClass}`}
              >
                <SafeHTML html={block.content} />
              </div>
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          {cta1Text ? (
            <Button
              to={cta1Link}
              variant={cta1Variant}
              size="md"
              className="min-w-[200px]"
            >
              {cta1Text}
            </Button>
          ):null}
          {cta2Text ? (
            <Button
              to={cta2Link}
              variant={cta2Variant}
              size="md"
              className="min-w-[200px]"
            >
              {cta2Text}
            </Button>
          ):null}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function Hero({ currentSeries, heroSlides = [], showScrollIndicator = true }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [timerKey, setTimerKey] = useState(0);

  const slides = heroSlides.length > 0
    ? heroSlides
    : [{ title: SITE_FULL_NAME, subtitle: currentSeries?.title || '' }];

  const currentSlide = slides[currentIndex] || slides[0];
  const slideDuration = (currentSlide?.slide_duration || DEFAULT_SLIDE_DURATION) * 1000;
  const hasMultipleSlides = slides.length > 1;
  const hasBackground = slides.some(s => s.background_image_url || s.background_video_url?.trim());

  // Parallax scroll - computed once at the parent level, shared with active slide
  const { scrollY } = useScroll();
  const parallaxDirection = currentSlide?.parallax_direction || 'down';
  const parallaxStrength = currentSlide?.parallax_strength || 0.5;
  const directionMultiplier = parallaxDirection === 'down' ? 1 : -1;
  const maxOffset = directionMultiplier * parallaxStrength * 200;
  const backgroundY = useTransform(scrollY, [0, 1000], [0, maxOffset]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setTimerKey((prev) => prev + 1);
  }, []);

  // Auto-advance timer
  useEffect(() => {
    if (!hasMultipleSlides || isPaused) return;

    const timer = setInterval(goToNext, slideDuration);
    return () => clearInterval(timer);
  }, [hasMultipleSlides, isPaused, slideDuration, goToNext, timerKey]);

  const scrollToContent = () => {
    const nextSection = document.querySelector('#service-times');
    const elementPosition = nextSection.getBoundingClientRect().top;
    const bannerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--alert-banner-height') || '0', 10);
    const headerOffset = 80 + bannerHeight;
    const offset = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence>
        <HeroSlide
          key={currentIndex}
          slide={currentSlide}
          currentSeries={currentSeries}
          backgroundY={backgroundY}
        />
      </AnimatePresence>

      {/* Slide Indicators */}
      {hasMultipleSlides && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex
                  ? (hasBackground ? 'bg-white scale-125' : 'bg-primary scale-125')
                  : (hasBackground ? 'bg-white/40 hover:bg-white/60' : 'bg-primary/30 hover:bg-primary/50')
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          onClick={scrollToContent}
          className={`absolute bottom-12 left-1/2 -translate-x-1/2 transition-colors cursor-pointer z-20 ${
            hasBackground
              ? 'text-white/50 hover:text-white/80'
              : 'text-primary/30 hover:text-primary/60'
          }`}
          aria-label="Scroll to content"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <FiArrowDown size={28} />
          </motion.div>
        </motion.button>
      )}
    </section>
  );
}
